/**
 * Project Control Plane — the operating summary that answers, in one place:
 * what is happening, what can start, what is blocked, what changed, what is
 * driving completion, and what needs a decision.
 *
 * Every section states its own availability. Feature-flagged intelligence
 * (requirements register, execution intelligence, scheduling agent, award
 * workflow) renders an explicit "not enabled" pill when the backend says
 * 503 not_enabled — a switched-off capability is never shown as a zero.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import apiClient from "../../services/api";
import {
  classifyWorkflowError,
  requirementsApi,
  executionIntelligenceApi,
  schedulingAgentApi,
  awardWorkflowApi,
} from "../../services/intelligenceService";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  SectionLoading,
  DisabledPill,
  PartialDataNotice,
  UnknownValue,
} from "../../components/workflow/WorkflowStates";
import { realismDisplay } from "../readiness/readinessPresentation";
import { rollupLabel, rollupTone, lookaheadItemsView } from "../lookahead/lookaheadPresentation";
import {
  listCount,
  statedCount,
  factLabel,
  ratioLabel,
  factTone,
  firstRowDetail,
} from "./controlPlanePresentation";
import { RecalculateButton } from "../readiness/RealismPanel";
import { canEditSchedule } from "../../lib/roles";
import { useAuth } from "../../App";
import PreconCommitPanel from "../precon/PreconCommitPanel";
import { recalculationSentence } from "../precon/preconPresentation";
import { presentRealismWorkSplit } from "../runtime/gateTruthPresentation";
import {
  CalendarClock,
  PlayCircle,
  OctagonAlert,
  ClipboardCheck,
  Boxes,
  Landmark,
  LifeBuoy,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";

/**
 * Tiny per-section fetch wrapper: data | classified error | loading.
 * A sequence counter drops late responses from a previous projectId so a
 * slow fetch can never overwrite a newer project's state.
 */
const useSection = (fetcher, deps) => {
  const [state, setState] = useState({ data: null, classified: null, loading: true });
  const seqRef = useRef(0);
  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    setState({ data: null, classified: null, loading: true });
    try {
      const data = await fetcher();
      if (seq === seqRef.current) setState({ data, classified: null, loading: false });
    } catch (err) {
      if (seq === seqRef.current)
        setState({ data: null, classified: classifyWorkflowError(err), loading: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    load();
  }, [load]);
  return { ...state, reload: load };
};

const fmtDate = (value) => {
  if (!value) return null;
  try {
    return format(typeof value === "string" ? parseISO(value) : value, "MMM d, yyyy");
  } catch {
    return String(value);
  }
};

/** A clickable metric row inside a section card. */
const MetricRow = ({ label, value, tone, onClick, detail }) => {
  const tones = {
    bad: "text-red-400",
    warn: "text-amber-400",
    good: "text-emerald-400",
    neutral: "text-foreground",
    // A figure nobody established is never coloured as news. Green on an
    // unknown "Blocked" would tell a superintendent the site is clear.
    unknown: "text-muted-foreground",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-left bg-muted/30 ${
        onClick ? "hover:bg-muted/50 cursor-pointer" : "cursor-default"
      }`}
    >
      <span className="text-sm text-muted-foreground min-w-0">
        {label}
        {detail && <span className="block text-xs text-muted-foreground/70 truncate">{detail}</span>}
      </span>
      <span className={`text-sm font-semibold shrink-0 ${tones[tone] || tones.neutral}`}>
        {value}
      </span>
    </button>
  );
};

const SectionCard = ({ icon: Icon, title, onOpen, openLabel, children, headerRight }) => (
  <Card className="border-border">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary" />
          {title}
        </CardTitle>
        <div className="flex items-center gap-2">
          {headerRight}
          {onOpen && (
            <Button variant="ghost" size="sm" onClick={onOpen} className="text-xs h-7 px-2">
              {openLabel || "Open"}
              <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
            </Button>
          )}
        </div>
      </div>
    </CardHeader>
    <CardContent className="space-y-2">{children}</CardContent>
  </Card>
);

/** Section body dispatcher for loading/disabled/error, else render children. */
const SectionBody = ({ section, children, disabledLabel }) => {
  if (section.loading) return <SectionLoading label="Loading…" />;
  if (section.classified?.kind === "disabled")
    return (
      <div className="py-4 flex justify-center">
        <DisabledPill label={disabledLabel || "Not enabled in this environment"} />
      </div>
    );
  if (section.classified)
    return (
      <div className="py-3 text-center space-y-2">
        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
          {section.classified.kind === "conflict"
            ? section.classified.message
            : "Failed to load."}
        </p>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={section.reload}>
          <RefreshCw className="w-3 h-3 mr-1" />
          Retry
        </Button>
      </div>
    );
  return children;
};

const OPEN_PACKAGE_STATUSES = new Set(["upcoming", "pending", "submitted"]);

const ControlPlaneTab = ({ projectId, activities = [], lookahead, lookaheadRead, onNavigateTab }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recalcBusy, setRecalcBusy] = useState(false);

  // Read-only realism assessment replaces the old analyze-on-load POST: the
  // finish dates, verdict, critical path, and negative float all come from
  // GET /realism, and nothing recomputes as a page-load side effect.
  const realism = useSection(() => executionIntelligenceApi.realism(projectId), [projectId]);
  const gates = useSection(() => requirementsApi.gates(projectId), [projectId]);
  const requirements = useSection(() => requirementsApi.listForProject(projectId), [projectId]);
  const materials = useSection(
    () => executionIntelligenceApi.materialAuthority(projectId),
    [projectId]
  );
  const recovery = useSection(() => schedulingAgentApi.listProposals(projectId), [projectId]);
  const packages = useSection(
    () => apiClient.get(`/bids`, { params: { project_id: projectId } }).then((r) => r.data),
    [projectId]
  );

  // Open EITL award decisions across this project's open packages. Depends on
  // the packages fetch; the award-workflow flag may independently be off.
  const [awardState, setAwardState] = useState({
    proposals: [],
    classified: null,
    loading: true,
  });
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (packages.loading) {
        // A project switch put packages back into loading — clear the old
        // project's award data so it can't linger under the new project.
        setAwardState({ proposals: [], classified: null, loading: true });
        return;
      }
      if (packages.classified || !Array.isArray(packages.data)) {
        setAwardState({ proposals: [], classified: null, loading: false });
        return;
      }
      const open = packages.data
        .filter((b) => OPEN_PACKAGE_STATUSES.has(b.status))
        .slice(0, 8);
      const proposals = [];
      let classified = null;
      for (const pkg of open) {
        try {
          const list = await awardWorkflowApi.listProposals(pkg.id);
          list
            .filter((p) => p.state === "PROPOSED")
            .forEach((p) => proposals.push({ ...p, packageTitle: pkg.title }));
        } catch (err) {
          classified = classifyWorkflowError(err);
          break; // one 503/failure applies to the whole surface
        }
      }
      if (!cancelled) setAwardState({ proposals, classified, loading: false });
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [packages.loading, packages.classified, packages.data]);

  // ---- Derived presentation values (all from backend-provided data) -------

  // Realism is the single source for finish dates, verdict, critical path,
  // and negative float — nothing is re-derived from planned activities here.
  const plannedFinish = useMemo(() => {
    const ends = activities
      .map((a) => a.end_dt_planned || a.end_date_planned)
      .filter(Boolean)
      .sort();
    return ends.length ? ends[ends.length - 1] : null;
  }, [activities]);

  const approvedFinish = realism.data?.approvedFinish || plannedFinish;
  const forecastFinish = realism.data?.forecastFinish || null;
  const varianceDays = realism.data?.varianceDays ?? null;
  const criticalPath = realism.data?.criticalPath || [];
  const negativeFloat = realism.data?.negativeFloat || [];
  const blockedWork = realism.data?.blockedWork || [];
  /**
   * `what-can-start-2` moved work that was never established-against out of
   * blockedWork and into notEstablishedWork. Rendering only the first would
   * show this project improving because truth got more precise — the two are
   * counted apart and never summed.
   */
  const workSplit = presentRealismWorkSplit(realism.data);
  const criticalReviews = realism.data?.criticalReviews || [];

  // Read through the presenter, not off the raw payload. `items` arriving as
  // anything other than an array used to throw here during render and take
  // the whole project screen down — this tab renders on EVERY visit.
  const rollup = lookaheadRead?.rollup;
  const lookaheadItems = lookaheadRead?.state === "READ" ? lookaheadRead.items : [];
  const lookaheadItemsState = lookaheadItemsView(lookaheadRead);
  const blockedItems = lookaheadItems.filter((i) => i.lookahead_status === "blocked");
  const readyItems = lookaheadItems.filter((i) => i.lookahead_status === "ready");

  const reqRows = requirements.data?.requirements || [];
  const inReview = reqRows.filter((r) =>
    ["SUBMITTED", "UNDER_REVIEW", "RESUBMITTED"].includes(r.status)
  );
  const assumptions = reqRows.filter((r) =>
    ["INFERRED", "UNVERIFIED"].includes(r.verificationStatus)
  );

  const openPackages = (Array.isArray(packages.data) ? packages.data : []).filter((b) =>
    OPEN_PACKAGE_STATUSES.has(b.status)
  );
  const openRecovery = (Array.isArray(recovery.data) ? recovery.data : []).filter(
    (p) => p.state === "PROPOSED"
  );

  const unavailable = [];
  [
    ["Project realism", realism],
    ["Requirements register", requirements],
    ["Material authority", materials],
    ["Recovery proposals", recovery],
  ].forEach(([name, s]) => {
    if (s.classified?.kind === "disabled") unavailable.push({ name, reason: "disabled" });
    else if (s.classified && s.classified.kind !== "conflict")
      unavailable.push({ name, reason: "failed" });
  });
  if (awardState.classified)
    unavailable.push({
      name: "Award workflow",
      reason: awardState.classified.kind === "disabled" ? "disabled" : "failed",
    });

  const decisionsNeeded = openRecovery.length + awardState.proposals.length;

  const recalculate = async () => {
    setRecalcBusy(true);
    try {
      const summary = await executionIntelligenceApi.recalculate(projectId);
      if (summary.skipped) {
        toast.warning(summary.reason || "Recalculation was skipped.");
      } else {
        // Composed from the payload. The old string asserted "no schedule
        // changes were applied" from a constant — the summary carries no
        // `applied` key at all, and the run DOES write readiness, ERS and
        // audit events, so a flat denial misdescribed it.
        toast.success(recalculationSentence(summary));
      }
      realism.reload();
      gates.reload();
      materials.reload();
      recovery.reload();
    } catch (err) {
      const c = classifyWorkflowError(err);
      toast.error(c.kind === "disabled" ? "Execution intelligence is not enabled here." : c.message);
    } finally {
      setRecalcBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {canEditSchedule(user) && (
        <div className="flex justify-end">
          <RecalculateButton onClick={recalculate} busy={recalcBusy} />
        </div>
      )}
      <PartialDataNotice unavailable={unavailable} />

      {decisionsNeeded > 0 && (
        <Card className="border-primary/40 bg-primary/5">
          <CardContent className="py-3 px-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Landmark className="w-4 h-4 text-primary" />
              <span className="font-medium">
                {decisionsNeeded} decision{decisionsNeeded === 1 ? "" : "s"} waiting on you
              </span>
              <span className="text-muted-foreground text-xs">
                {awardState.proposals.length > 0 &&
                  `${awardState.proposals.length} award decision${
                    awardState.proposals.length === 1 ? "" : "s"
                  }`}
                {awardState.proposals.length > 0 && openRecovery.length > 0 && " · "}
                {openRecovery.length > 0 &&
                  `${openRecovery.length} recovery proposal${openRecovery.length === 1 ? "" : "s"}`}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => navigate("/control-tower")}
            >
              Review
              <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* -------- Schedule health (realism-driven) -------- */}
        <SectionCard
          icon={CalendarClock}
          title="Schedule Health"
          onOpen={() => navigate(`/projects/${projectId}/gantt`)}
          openLabel="Gantt"
          headerRight={
            realism.data && (
              <span className="flex items-center gap-1.5">
                <Badge
                  variant="outline"
                  className={`${realismDisplay(realism.data.classification).className} font-medium`}
                  title={realism.data.basis || undefined}
                >
                  {realismDisplay(realism.data.classification).label}
                </Badge>
              </span>
            )
          }
        >
          <MetricRow
            label="Approved plan finish"
            value={fmtDate(approvedFinish) || <UnknownValue reason="No approved finish date on record." />}
          />
          <SectionBody section={realism} disabledLabel="Execution intelligence not enabled">
            <MetricRow
              label="Forecast finish"
              value={
                forecastFinish ? (
                  <span className="inline-flex items-center gap-1">
                    {fmtDate(forecastFinish)}
                    {varianceDays !== null && varianceDays !== 0 && (
                      <Badge
                        variant="outline"
                        className={
                          varianceDays > 0
                            ? "bg-red-500/20 text-red-400 border-red-500/30"
                            : "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                        }
                      >
                        {varianceDays > 0 ? `+${varianceDays}d` : `${varianceDays}d`}
                      </Badge>
                    )}
                  </span>
                ) : (
                  <UnknownValue reason="The realism assessment did not resolve a forecast finish." />
                )
              }
            />
            <MetricRow
              label="Can finish on time"
              value={realism.data?.canFinishOnTime || "UNKNOWN"}
              tone={
                realism.data?.canFinishOnTime === "YES"
                  ? "good"
                  : realism.data?.canFinishOnTime === "NO"
                    ? "bad"
                    : "neutral"
              }
              detail={realism.data?.basis}
              onClick={() => onNavigateTab?.("readiness")}
            />
            <MetricRow
              label="Critical path activities"
              value={criticalPath.length}
              tone={criticalPath.length ? "warn" : "good"}
              onClick={() => navigate(`/projects/${projectId}/gantt`)}
            />
            <MetricRow
              label="Negative float"
              value={negativeFloat.length}
              tone={negativeFloat.length ? "bad" : "good"}
              onClick={() => navigate("/control-tower")}
            />
            <MetricRow
              label="Unresolved reviews on the critical path"
              value={criticalReviews.filter((r) => r.onCriticalPath).length}
              tone={criticalReviews.filter((r) => r.onCriticalPath).length ? "warn" : "good"}
              detail={
                criticalReviews.length
                  ? `${criticalReviews.length} unresolved review${criticalReviews.length === 1 ? "" : "s"} total`
                  : undefined
              }
              onClick={() => onNavigateTab?.("requirements")}
            />
          </SectionBody>
        </SectionCard>

        {/* -------- Work readiness -------- */}
        <SectionCard
          icon={PlayCircle}
          title="Work Status"
          onOpen={() => onNavigateTab?.("lookahead")}
          openLabel="Lookahead"
        >
          {lookaheadItemsState.kind === "LOADING" ? (
            <SectionLoading />
          ) : lookaheadItemsState.kind === "REFUSED" ||
            lookaheadItemsState.kind === "UNREADABLE" ? (
            <p
              className="text-xs text-muted-foreground py-3 text-center flex items-center justify-center gap-1"
              role="status"
              data-testid="control-plane-lookahead-refusal"
              data-state={lookaheadItemsState.kind}
            >
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" aria-hidden="true" />
              {lookaheadItemsState.body} Reload the page to retry.
            </p>
          ) : (
            <>
              <MetricRow
                label="Ready to start now"
                value={rollupLabel(rollup?.ready)}
                tone={rollupTone(rollup?.ready, { zeroIs: "neutral", nonZeroIs: "good" })}
                detail={readyItems.slice(0, 2).map((i) => i.name).join(", ")}
                onClick={() => onNavigateTab?.("lookahead")}
              />
              <MetricRow
                label="Blocked"
                value={rollupLabel(rollup?.blocked)}
                tone={rollupTone(rollup?.blocked, { zeroIs: "good", nonZeroIs: "bad" })}
                detail={
                  blockedItems[0]?.blockers?.[0]
                    ? `${blockedItems[0].name}: waiting on ${blockedItems[0].blockers[0].predecessor_name}`
                    : blockedItems[0]?.name
                }
                onClick={() => onNavigateTab?.("lookahead")}
              />
              <MetricRow
                label="In progress"
                value={rollupLabel(rollup?.in_progress)}
                onClick={() => onNavigateTab?.("lookahead")}
              />
              <MetricRow
                label="Blocked work (realism)"
                value={realism.classified ? <DisabledPill label="n/a" /> : blockedWork.length}
                tone={blockedWork.length ? "warn" : "neutral"}
                onClick={() => onNavigateTab?.("readiness")}
              />
              {/* Withheld for a different reason, and never added to the
                  count above: the backend publishes no combined figure. */}
              {!realism.classified && workSplit?.notEstablishedStated ? (
                <MetricRow
                  label="Not-established work (realism)"
                  value={workSplit.notEstablishedCount}
                  tone={workSplit.notEstablishedCount ? "warn" : "neutral"}
                  onClick={() => onNavigateTab?.("readiness")}
                />
              ) : null}
            </>
          )}
        </SectionCard>

        {/* -------- Requirements -------- */}
        <SectionCard
          icon={ClipboardCheck}
          title="Requirements"
          onOpen={() => onNavigateTab?.("requirements")}
          openLabel="Register"
        >
          <SectionBody section={gates} disabledLabel="Requirements register not enabled">
            <MetricRow
              label="Open hard gates"
              value={factLabel(listCount(gates.data?.hardGates))}
              tone={factTone(listCount(gates.data?.hardGates), { zeroIs: "good", nonZeroIs: "bad" })}
              detail={firstRowDetail(gates.data?.hardGates, "title")}
              onClick={() => onNavigateTab?.("requirements")}
            />
            <MetricRow
              label="Submittals / reviews in flight"
              value={requirements.loading ? "…" : requirements.classified ? "n/a" : inReview.length}
              tone={inReview.length ? "warn" : "neutral"}
              onClick={() => onNavigateTab?.("requirements")}
            />
            <MetricRow
              label="Needs verification (assumptions)"
              value={requirements.loading ? "…" : requirements.classified ? "n/a" : assumptions.length}
              tone={assumptions.length ? "warn" : "good"}
              onClick={() => onNavigateTab?.("requirements")}
            />
            <MetricRow
              label="Conflicts"
              value={factLabel(listCount(gates.data?.conflicts))}
              tone={factTone(listCount(gates.data?.conflicts), { zeroIs: "good", nonZeroIs: "bad" })}
              onClick={() => onNavigateTab?.("requirements")}
            />
          </SectionBody>
        </SectionCard>

        {/* -------- Materials -------- */}
        <SectionCard
          icon={Boxes}
          title="Materials"
          onOpen={() => navigate(`/procurement?project_id=${projectId}`)}
          openLabel="Procurement"
        >
          <SectionBody section={materials} disabledLabel="Material authority not enabled">
            <MetricRow
              label="Delivery dates resolved"
              value={ratioLabel(
                statedCount(materials.data?.resolvedCount),
                statedCount(materials.data?.count),
              )}
              tone="neutral"
            />
            <MetricRow
              label="Conflicting evidence"
              value={factLabel(statedCount(materials.data?.conflictCount))}
              tone={factTone(statedCount(materials.data?.conflictCount), {
                zeroIs: "good",
                nonZeroIs: "bad",
              })}
            />
            <MetricRow
              label="Unknown delivery"
              value={factLabel(statedCount(materials.data?.unknownCount))}
              tone={factTone(statedCount(materials.data?.unknownCount), {
                zeroIs: "good",
                nonZeroIs: "warn",
              })}
            />
          </SectionBody>
        </SectionCard>

        {/* -------- Commercial -------- */}
        <SectionCard
          icon={Landmark}
          title="Procurement & Decisions"
          onOpen={() => navigate(`/procurement?project_id=${projectId}`)}
          openLabel="Procurement"
        >
          <SectionBody section={packages}>
            <MetricRow
              label="Open bid packages / RFQs"
              value={openPackages.length}
              detail={openPackages[0]?.title}
              onClick={() => navigate(`/procurement?project_id=${projectId}`)}
            />
            <MetricRow
              label="Award decisions awaiting EITL"
              value={
                awardState.loading ? (
                  "…"
                ) : awardState.classified?.kind === "disabled" ? (
                  <DisabledPill />
                ) : awardState.classified ? (
                  "n/a"
                ) : (
                  awardState.proposals.length
                )
              }
              tone={awardState.proposals.length ? "warn" : "neutral"}
              detail={awardState.proposals[0]?.packageTitle}
              onClick={() => navigate(`/procurement?project_id=${projectId}`)}
            />
          </SectionBody>
        </SectionCard>

        {/* -------- Recovery -------- */}
        <SectionCard
          icon={LifeBuoy}
          title="Recovery"
          onOpen={() => navigate("/control-tower")}
          openLabel="Control Tower"
        >
          <SectionBody section={recovery} disabledLabel="Scheduling agent not enabled">
            <MetricRow
              label="Recovery proposals awaiting decision"
              value={openRecovery.length}
              tone={openRecovery.length ? "warn" : "good"}
              detail={
                openRecovery[0] &&
                `${openRecovery[0].proposalKind || "RESEQUENCE"} · ${
                  openRecovery[0].trigger || ""
                }`
              }
              onClick={() => navigate("/control-tower")}
            />
            {openRecovery.length === 0 && !recovery.classified && (
              <p className="text-xs text-muted-foreground px-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                No recovery proposals are waiting. New ones appear here when the
                scheduling agent detects a slip.
              </p>
            )}
          </SectionBody>
        </SectionCard>

        {/* -------- Blocked work detail -------- */}
        <SectionCard
          icon={OctagonAlert}
          title="Blocked Work"
          onOpen={() => onNavigateTab?.("lookahead")}
          openLabel="Detail"
        >
          {lookaheadItemsState.kind === "LOADING" ? (
            <SectionLoading />
          ) : lookaheadItemsState.kind === "REFUSED" ||
            lookaheadItemsState.kind === "UNREADABLE" ? (
            <p
              className="text-xs text-muted-foreground py-3 text-center"
              role="status"
              data-testid="control-plane-blocked-unknown"
              data-state={lookaheadItemsState.kind}
            >
              The lookahead could not be read — blocked-work status is unknown, not clear.
            </p>
          ) : blockedItems.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3 text-center">
              Nothing in the lookahead window is blocked.
            </p>
          ) : (
            blockedItems.slice(0, 4).map((item) => (
              <div key={item.id} className="rounded-lg bg-muted/30 px-3 py-2">
                <p className="text-sm font-medium">{item.name}</p>
                <p className="text-xs text-muted-foreground">
                  {item.blockers?.length
                    ? `Waiting on ${item.blockers
                        .map((b) => `${b.predecessor_name} (${b.predecessor_status})`)
                        .join(", ")}`
                    : "Blocked — no predecessor recorded; check constraints."}
                </p>
              </div>
            ))
          )}
        </SectionCard>
      </div>

      {/* The governed preconstruction decision, on the project's own control
          surface: analyse, review what a commitment does and does not do,
          confirm deliberately, and read exactly what was recorded. */}
      <PreconCommitPanel projectId={projectId} user={user} />
    </div>
  );
};

export default ControlPlaneTab;
